'use client';

import { MotionConfigContext } from 'motion/react';
import type { SquishExpression, SquishShape } from '@usespaceui/squishmoji';
import { useContext, useId, useRef, useSyncExternalStore } from 'react';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/use-media-query';
import {
  shouldAnimateAgentActivity,
  type AgentActivityState,
} from './agent-activity-state';
import styles from './agent-activity-mark.module.css';
import { useAgentActivityInView } from './use-agent-activity-in-view';
import { AgentActivityShape } from './agent-activity-shape';

function subscribeVisibility(notify: () => void) {
  document.addEventListener('visibilitychange', notify);
  return () => document.removeEventListener('visibilitychange', notify);
}
const getVisibility = () => document.visibilityState === 'visible';
const getServerVisibility = () => false;
const noVisibilitySubscription = () => () => {};

/** IMAI's square-first identity with library-driven active shape morphs.
 * Decorative only: the owning status supplies accessible text and any specific progress detail.
 * The body adapter owns geometry; React updates only for semantic/visibility changes.
 */
export function AgentActivityMark({
  state,
  animate = true,
  variant = 'expressive',
  emotion = 'focused',
  context = 'chat',
  activeFill = false,
  activePalette = 'multicolor',
  staticShape,
  staticExpression,
  className,
}: {
  state: AgentActivityState;
  animate?: boolean;
  variant?: 'expressive' | 'focused';
  emotion?: 'focused' | 'loving' | 'doubtful';
  /** Sidebar lacks a persistent status label; chat already supplies one. */
  context?: 'chat' | 'sidebar';
  /** Keep the shared active gradient when a compact surface pauses its morph. */
  activeFill?: boolean;
  activePalette?: 'multicolor' | 'green';
  /** Optional still pose for surfaces using the shared morphing mark. */
  staticShape?: SquishShape;
  staticExpression?: SquishExpression;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const activeCutoutId = useId();
  const showActiveIndicator =
    context === 'sidebar' &&
    variant === 'expressive' &&
    (state === 'thinking' || state === 'working');
  const canMove = animate && (state === 'thinking' || state === 'working');
  // Focused entry may finish while its eye motion is paused by a stop request.
  const observesVisibility = canMove || variant === 'focused';
  const inView = useAgentActivityInView(ref, observesVisibility);
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const motionConfig = useContext(MotionConfigContext);
  const motionDisabled =
    Boolean(reducedMotion) || motionConfig.reducedMotion === 'always';
  const pageVisible = useSyncExternalStore(
    observesVisibility ? subscribeVisibility : noVisibilitySubscription,
    getVisibility,
    getServerVisibility
  );
  const running = shouldAnimateAgentActivity({
    state,
    animate,
    reducedMotion: motionDisabled,
    inView,
    pageVisible,
  });

  // Completed history skips visibility subscriptions and shares the motion preference.
  const hoverEnabled = state === 'completed' && animate && !motionDisabled;
  const entryRunning = inView && pageVisible && !motionDisabled;

  return (
    <span
      ref={ref}
      aria-hidden="true"
      data-agent-activity={state}
      data-agent-context={context}
      data-agent-variant={variant}
      data-agent-emotion={variant === 'focused' ? emotion : undefined}
      data-agent-hover-animation={hoverEnabled ? 'enabled' : undefined}
      data-agent-animation={running ? 'running' : 'paused'}
      data-agent-entry-animation={
        variant === 'focused'
          ? entryRunning
            ? 'running'
            : 'paused'
          : undefined
      }
      data-agent-reduced-motion={motionDisabled ? 'true' : undefined}
      className={cn(
        styles.mark,
        'inline-flex shrink-0 items-center justify-center',
        context === 'chat' && variant === 'expressive' ? 'size-6' : 'size-4',
        className
      )}
    >
      <svg
        viewBox="0 0 32 32"
        fill="none"
        focusable="false"
        className="size-full"
      >
        {showActiveIndicator && (
          <defs>
            <mask
              id={activeCutoutId}
              className={styles.activityCutout}
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width="32"
              height="32"
            >
              <rect width="32" height="32" fill="white" />
              <circle cx="27" cy="27" r="4.75" fill="black" />
            </mask>
          </defs>
        )}
        <g mask={showActiveIndicator ? `url(#${activeCutoutId})` : undefined}>
          {variant === 'expressive' ? (
            <AgentActivityShape
              running={running}
              // Active marks keep their gradient while paused (not yet
              // observed after a remount, or scrolled away), so they never
              // flash the flat fallback fill (a white square in dark mode).
              activeFill={canMove || activeFill}
              palette={activePalette}
              gradient={context !== 'sidebar'}
              staticShape={staticShape}
              staticExpression={staticExpression}
              staticEyes={Boolean(staticShape)}
              className={styles.surface}
            />
          ) : (
            <rect
              className={styles.surface}
              x="3"
              y="3"
              width="26"
              height="26"
              rx="6"
              strokeWidth="1.5"
            />
          )}
          {variant === 'expressive' &&
          (running || staticShape) ? null : variant === 'focused' &&
            emotion !== 'focused' ? (
            <g data-agent-stop-expression="true">
              <path
                data-agent-loving-eyes="true"
                className={styles.lovingEyes}
                d="M11.5 20 C9.5 18 7 16.5 7.5 14 C8 11 10.5 11 11.5 13 C12.5 11 15 11 15.5 14 C16 16.5 13.5 18 11.5 20 M21 20 C19 18 16.5 16.5 17 14 C17.5 11 20 11 21 13 C22 11 24.5 11 25 14 C25.5 16.5 23 18 21 20"
              />
              <path
                data-agent-doubtful-eyes="true"
                className={styles.doubtfulEyes}
                d="M9 13 Q11 11 13 13 M19 14 L23 16 M11 16 L11 19 M21 18 L21 19"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </g>
          ) : state === 'completed' ? (
            <path
              data-agent-happy-eyes="true"
              className={styles.happyEyes}
              d="M10 16 Q12 12 14 16 M18 16 Q20 12 22 16"
              stroke="var(--primary-foreground)"
              strokeWidth="2"
              strokeLinecap="round"
            />
          ) : (
            <g className={styles.gaze}>
              <rect
                className={styles.eye}
                data-agent-calm-eye="true"
                x="10.5"
                y="13"
                width="3"
                height="4"
                rx="1.5"
              />
              <rect
                className={styles.eye}
                data-agent-calm-eye="true"
                x="18.5"
                y="13"
                width="3"
                height="4"
                rx="1.5"
              />
            </g>
          )}
        </g>
        {showActiveIndicator && (
          <circle
            data-agent-active-indicator="true"
            className={styles.activeIndicator}
            cx="27"
            cy="27"
            r="3.25"
          />
        )}
      </svg>
    </span>
  );
}
